import { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { CheckCircle2, AlertTriangle, Sparkles, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function EmailConfirm() {
  const [sp] = useSearchParams();
  const token = sp.get("token");
  const [state, setState] = useState("loading"); // loading | success | error
  const [message, setMessage] = useState("");
  const [email, setEmail] = useState("");
  const nav = useNavigate();

  useEffect(() => {
    if (!token) { setState("error"); setMessage("Missing confirmation token."); return; }
    (async () => {
      try {
        const { data } = await api.get("/auth/confirm-email", { params: { token } });
        setEmail(data.email || "");
        setState("success");
        // auto-redirect to home after 2.5s
        setTimeout(() => nav("/"), 2500);
      } catch (err) {
        setState("error");
        setMessage(formatApiErrorDetail(err.response?.data?.detail) || "Confirmation failed");
      }
    })();
  }, [token, nav]);

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
      <div className="max-w-md w-full bg-white border border-slate-200 rounded-md p-8 text-center" data-testid="email-confirm">
        <Link to="/" className="inline-flex items-center gap-2 mb-6">
          <div className="w-7 h-7 bg-slate-950 rounded-sm flex items-center justify-center">
            <Sparkles className="w-4 h-4 text-cyan-400" strokeWidth={2.5} />
          </div>
          <div className="font-display font-bold text-slate-900">CogniTrust<span className="text-cyan-600">.</span>AI</div>
        </Link>
        {state === "loading" && (
          <>
            <Loader2 className="w-10 h-10 text-cyan-600 animate-spin mx-auto" />
            <h1 className="font-display text-2xl font-bold text-slate-900 mt-4">Confirming your email…</h1>
          </>
        )}
        {state === "success" && (
          <>
            <div className="w-14 h-14 bg-emerald-100 text-emerald-700 rounded-full flex items-center justify-center mx-auto">
              <CheckCircle2 className="w-7 h-7" />
            </div>
            <h1 className="font-display text-2xl font-bold text-slate-900 mt-4" data-testid="confirm-success">Email confirmed!</h1>
            <p className="text-slate-600 mt-2 text-sm">{email && <>Thank you for confirming <b>{email}</b>. </>}Your application is now with our admin team for review. We'll email you once you're approved.</p>
            <p className="text-xs text-slate-500 mt-4">Redirecting to homepage…</p>
            <Button onClick={()=>nav("/")} className="mt-5 bg-slate-900 hover:bg-slate-800" data-testid="btn-go-home">Go to homepage</Button>
          </>
        )}
        {state === "error" && (
          <>
            <div className="w-14 h-14 bg-red-100 text-red-700 rounded-full flex items-center justify-center mx-auto">
              <AlertTriangle className="w-7 h-7" />
            </div>
            <h1 className="font-display text-2xl font-bold text-slate-900 mt-4" data-testid="confirm-error">Confirmation failed</h1>
            <p className="text-slate-600 mt-2 text-sm">{message}</p>
            <Button onClick={()=>nav("/")} className="mt-5 bg-slate-900 hover:bg-slate-800" data-testid="btn-go-home">Back to home</Button>
          </>
        )}
      </div>
    </div>
  );
}
